import React, { useState } from 'react';
import { useNotificationAnalytics } from '../hooks/useAnalytics';
import { AnalyticsFilters } from '../types/analytics.types';
import { KPICard } from './KPICard';
import './NotificationsTab.css';

interface NotificationsTabProps {
  filters: AnalyticsFilters;
  onFiltersChange: (filters: AnalyticsFilters) => void;
}

export const NotificationsTab: React.FC<NotificationsTabProps> = ({ filters, onFiltersChange }) => {
  const { data, loading, error, refetch } = useNotificationAnalytics(filters);
  const [expandedType, setExpandedType] = useState<string | null>(null);

  const toggleExpanded = (type: string) => {
    setExpandedType(expandedType === type ? null : type);
  };

  if (error) {
    return (
      <div className="notifications-tab">
        <div className="error-message">
          <h3>❌ Error Loading Notification Data</h3>
          <p>{error.message}</p>
          <button onClick={() => refetch()} className="retry-button">
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-tab">
      {/* Summary KPIs */}
      <div className="notifications-summary">
        <h2>Notifications Overview</h2>
        <p className="metric-description">
          📊 Success metric: User opened the app within 3 hours of receiving the notification
        </p>
        <div className="kpi-grid">
          {loading ? (
            <>
              <KPICard title="Total Sent" value="0" loading={true} />
              <KPICard title="Success Rate" value="0%" loading={true} />
              <KPICard title="Delivery Rate" value="0%" loading={true} />
              <KPICard title="Users Reached" value="0" loading={true} />
            </>
          ) : data?.summary ? (
            <>
              <KPICard
                title="Total Sent"
                value={data.summary.total_sent.toLocaleString()}
                subtitle="Notifications delivered"
                icon="📤"
              />
              <KPICard
                title="Success Rate"
                value={`${data.summary.overall_open_rate.toFixed(1)}%`}
                subtitle={`${data.summary.total_opened} app opens within 3h`}
                icon="✅"
              />
              <KPICard
                title="Delivery Rate"
                value={`${data.delivery?.overall_delivery_rate.toFixed(1) || 0}%`}
                subtitle={`${data.delivery?.total_delivered || 0} delivered`}
                icon="📨"
              />
              <KPICard
                title="Users Reached"
                value={data.summary.unique_users_reached.toLocaleString()}
                subtitle="Unique users"
                icon="👥"
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Platform Performance */}
      {data?.delivery && data.delivery.by_platform.length > 0 && (
        <div className="notifications-section">
          <h3>📱 Platform Performance</h3>
          <div className="platform-grid">
            {data.delivery.by_platform.map((platform) => (
              <div key={platform.platform} className="platform-card">
                <div className="platform-header">
                  <span className="platform-icon">{platform.platform === 'ios' ? '🍎' : '🤖'}</span>
                  <span className="platform-name">{platform.platform.toUpperCase()}</span>
                </div>
                <div className="platform-stats">
                  <div className="stat-row">
                    <span className="stat-label">Sent:</span>
                    <span className="stat-value">{platform.sent.toLocaleString()}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Delivered:</span>
                    <span className="stat-value">{platform.delivered.toLocaleString()}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Delivery Rate:</span>
                    <span className={`stat-value rate ${
                      platform.delivery_rate >= 95 ? 'high' : platform.delivery_rate >= 85 ? 'medium' : 'low'
                    }`}>
                      {platform.delivery_rate.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Engagement Metrics */}
      {data?.engagement && (
        <div className="notifications-section">
          <h3>💬 Engagement Metrics</h3>
          <p className="section-note">App opens within 3 hours of notification</p>
          <div className="engagement-stats">
            <div className="eng-stat">
              <div className="eng-icon opened">✅</div>
              <div className="eng-content">
                <div className="eng-value">{data.engagement.opened.toLocaleString()}</div>
                <div className="eng-label">Successful (3h)</div>
              </div>
            </div>
            <div className="eng-stat">
              <div className="eng-icon ignored">❌</div>
              <div className="eng-content">
                <div className="eng-value">{data.engagement.ignored.toLocaleString()}</div>
                <div className="eng-label">No App Open</div>
              </div>
            </div>
            <div className="eng-stat">
              <div className="eng-icon rate">📊</div>
              <div className="eng-content">
                <div className="eng-value">{data.engagement.open_rate.toFixed(1)}%</div>
                <div className="eng-label">Success Rate</div>
              </div>
            </div>
          </div>

          {/* Best Hours */}
          {data.engagement.best_performing_hours.length > 0 && (
            <div className="best-hours">
              <h4>⏰ Best Performing Hours</h4>
              <div className="hours-list">
                {data.engagement.best_performing_hours.map((hour) => (
                  <div key={hour.hour} className="hour-item">
                    <span className="hour-time">{hour.hour}:00</span>
                    <div className="hour-bar">
                      <div
                        className="hour-fill"
                        style={{ width: `${hour.open_rate}%` }}
                      ></div>
                    </div>
                    <span className="hour-rate">{hour.open_rate.toFixed(1)}%</span>
                    <span className="hour-count">({hour.opened}/{hour.total})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notification Types Performance */}
      {data?.types && data.types.by_type.length > 0 && (
        <div className="notifications-section">
          <h3>🔔 Notification Types Performance</h3>
          <p className="section-note">Success = App opened within 3 hours. Click on a type to see individual notification messages.</p>
          <div className="types-table">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '30px' }}></th>
                  <th>Type</th>
                  <th>Sent</th>
                  <th>Successful (3h)</th>
                  <th>No App Open</th>
                  <th>Success Rate</th>
                </tr>
              </thead>
              <tbody>
                {data.types.by_type.slice(0, 10).map((type) => (
                  <React.Fragment key={type.type}>
                    <tr
                      className={`type-row ${expandedType === type.type ? 'expanded' : ''}`}
                      onClick={() => toggleExpanded(type.type)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="expand-icon">
                        {type.individual_notifications && type.individual_notifications.length > 0 ? (
                          expandedType === type.type ? '▼' : '▶'
                        ) : ''}
                      </td>
                      <td className="type-name">{type.type}</td>
                      <td>{type.sent.toLocaleString()}</td>
                      <td className="opened-count">{type.opened.toLocaleString()}</td>
                      <td className="ignored-count">{type.ignored.toLocaleString()}</td>
                      <td>
                        <span className={`rate-badge ${
                          type.open_rate >= 50 ? 'high' : type.open_rate >= 30 ? 'medium' : 'low'
                        }`}>
                          {type.open_rate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                    {expandedType === type.type && type.individual_notifications && (
                      <tr className="expanded-content">
                        <td colSpan={6}>
                          <div className="individual-notifications">
                            <h4>Individual Notification Messages for {type.type}</h4>
                            <table className="individual-table">
                              <thead>
                                <tr>
                                  <th>Title</th>
                                  <th>Body</th>
                                  <th>Sent</th>
                                  <th>Success (3h)</th>
                                  <th>No Open</th>
                                  <th>Success Rate</th>
                                </tr>
                              </thead>
                              <tbody>
                                {type.individual_notifications.map((notif, idx) => (
                                  <tr key={idx}>
                                    <td className="notif-title">{notif.title}</td>
                                    <td className="notif-body">{notif.body}</td>
                                    <td>{notif.sent.toLocaleString()}</td>
                                    <td className="opened-count">{notif.opened.toLocaleString()}</td>
                                    <td className="ignored-count">{notif.ignored.toLocaleString()}</td>
                                    <td>
                                      <span className={`rate-badge ${
                                        notif.open_rate >= 50 ? 'high' : notif.open_rate >= 30 ? 'medium' : 'low'
                                      }`}>
                                        {notif.open_rate.toFixed(1)}%
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Key Insights */}
      {data?.summary && !loading && (
        <div className="notifications-insights">
          <h3>💡 Key Insights</h3>
          <div className="insights-grid">
            <div className="insight-card">
              <span className="insight-icon">📊</span>
              <div className="insight-content">
                <h4>Overall Performance</h4>
                <p>
                  Sent {data.summary.total_sent.toLocaleString()} notifications reaching{' '}
                  {data.summary.unique_users_reached.toLocaleString()} unique users with a{' '}
                  {data.summary.overall_open_rate.toFixed(1)}% open rate
                </p>
              </div>
            </div>

            {data.delivery && (
              <div className="insight-card">
                <span className="insight-icon">✅</span>
                <div className="insight-content">
                  <h4>Delivery Success</h4>
                  <p>
                    {data.delivery.overall_delivery_rate.toFixed(1)}% delivery rate across{' '}
                    {data.summary.platforms_used.join(' and ')} platforms
                  </p>
                </div>
              </div>
            )}

            {data.engagement && data.engagement.best_performing_hours.length > 0 && (
              <div className="insight-card">
                <span className="insight-icon">⏰</span>
                <div className="insight-content">
                  <h4>Optimal Send Time</h4>
                  <p>
                    Best engagement at {data.engagement.best_performing_hours[0].hour}:00 with{' '}
                    {data.engagement.best_performing_hours[0].open_rate.toFixed(1)}% open rate
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
